import type { ChatEntry } from "@ark-bot/protocol";

import { text } from "./i18n.js";
import { CHAT_TIME } from "./text/chat.js";

/**
 * The chat's time stamps, as the prototype places them: not on every bubble
 * but as a line between runs of messages, "Today 2:24 AM", whenever the
 * conversation paused long enough that the reader has lost track of when.
 * Days are the days of the zone the page shows (time-zone.ts), not the
 * device's, so "Today" means today where the user said they live.
 */

/** A pause this long puts a stamp before the next message. */
export const STAMP_GAP_MS = 15 * 60_000;

/** The calendar day `at` falls on in `timeZone` (or the device's zone), as year, month and day. */
function dayOf(at: number, timeZone?: string): { readonly year: number; readonly month: number; readonly day: number } {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "numeric", day: "numeric" }).formatToParts(at);
  const part = (type: string): number => Number(parts.find((one) => one.type === type)?.value ?? "0");
  return { year: part("year"), month: part("month"), day: part("day") };
}

const key = (day: { year: number; month: number; day: number }): string => `${day.year}-${day.month}-${day.day}`;

/**
 * "Today 2:24 AM", "Yesterday 9:10 PM", then "Wed 2 Sep, 10:00 AM", with the
 * year once it is not this one; in Indonesian "Hari ini 14.24". The words
 * follow the app's language, the date and time `locale`.
 */
export function stampLabel(at: number, now: number, locale?: string, timeZone?: string): string {
  const when = dayOf(at, timeZone);
  const today = dayOf(now, timeZone);
  // The day before today's date, by the calendar rather than by 24 hours, which a clock change would miss.
  const before = new Date(Date.UTC(today.year, today.month - 1, today.day - 1));
  const yesterday = { year: before.getUTCFullYear(), month: before.getUTCMonth() + 1, day: before.getUTCDate() };
  const time = new Date(at).toLocaleTimeString(locale, { timeZone, hour: "numeric", minute: "2-digit" });
  const t = text(CHAT_TIME);
  if (key(when) === key(today)) return t.today(time);
  if (key(when) === key(yesterday)) return t.yesterday(time);
  const day = new Date(at).toLocaleDateString(locale, { timeZone, weekday: "short", day: "numeric", month: "short", ...(when.year === today.year ? {} : { year: "numeric" }) });
  return t.dayAndTime(day, time);
}

/** The stamp to draw before `entry`, or null when it follows its predecessor closely enough to need none. */
export function stampBefore(previous: ChatEntry | undefined, entry: ChatEntry, now: number, locale?: string, timeZone?: string): string | null {
  if (previous != null && entry.createdAt - previous.createdAt < STAMP_GAP_MS) return null;
  return stampLabel(entry.createdAt, now, locale, timeZone);
}
